/**
 * option-shuffle.ts — Option C 救火 fix 3/3
 *
 * Seeded MCQ option permutation. Same seed ⇒ same shuffle ⇒ test-friendly
 * and 重玩 deterministic. Decoupled from overlay.ts so engine / lesson runner
 * / exam composer can call it without dragging in DOM dependencies, and so
 * tests can verify shuffle behaviour in isolation.
 *
 * Algorithm: Fisher-Yates on an index array using mulberry32(hash(seed)).
 * Returns BOTH the permuted options AND the displayIdx→originalIdx map so
 * callers can keep the original `correctAnswer` index unchanged (avoiding
 * mutation of shared question objects across retries).
 */

import { makeSeededRng } from '../content/bank-loader';

export interface ShuffleResult<T> {
  /** Options in their new display order. */
  displayOptions: T[];
  /** displayIdx → originalIdx (so a click on display-position N submits originalIdx). */
  displayToOriginal: number[];
}

export function shuffleMcqOptions<T>(
  options: T[],
  seed: string | number,
): ShuffleResult<T> {
  const n = options.length;
  const indices: number[] = options.map((_, i) => i);

  // Edge cases: 0 or 1 options — nothing to shuffle.
  if (n <= 1) {
    return { displayOptions: options.slice(), displayToOriginal: indices };
  }

  const rand = makeSeededRng(seed);
  // Fisher-Yates on the index array.
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }

  return {
    displayOptions: indices.map(i => options[i]),
    displayToOriginal: indices,
  };
}
